import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { getSplit } from '$lib/api/splits';
import { Button } from '$lib/components/ui/button';
import * as Card from '$lib/components/ui/card';
import { addToast } from '$lib/stores/toastStore.svelte';
import { route, navigate } from '$lib/router';
import { saveLastSplit } from '$lib/stores/lastSplitStore';
import { getCachedSplitName, setCachedSplitName } from '$lib/stores/splitTitleCache';
import ParticipantSummaryCard from '$lib/components/participant/ParticipantSummaryCard.svelte';
import SplitPageHeader from '$lib/components/ui/split-page-header/SplitPageHeader.svelte';

var root_2 = $.from_html(`<div class="flex flex-col items-center justify-center py-12 space-y-4"><svg class="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> <p class="text-muted-foreground">Loading split...</p></div>`);
var root_8 = $.from_html(`<p class="text-muted-foreground"> </p> <!>`, 1);
var root_5 = $.from_html(`<!> <!>`, 1);
var root_13 = $.from_html(`<div class="flex items-center justify-between"><div><p class="text-sm text-muted-foreground">Expenses</p> <p class="text-lg font-semibold"> </p></div> <div class="text-right"><p class="text-sm text-muted-foreground">Total</p> <p class="text-lg font-semibold text-primary"> </p></div></div>`);
var root_16 = $.from_html(`<div class="flex items-center justify-between"><div><p class="text-sm text-muted-foreground">Settlement</p> <p class="text-lg font-semibold"> </p></div> <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"></path></svg></div>`);
var root_14 = $.from_html(`<section class="w-full"><button class="w-full text-left" aria-label="View settlement"><!></button></section>`);
var root_11 = $.from_html(`<section class="w-full"><button class="w-full text-left" aria-label="View all expenses"><!></button></section> <section class="w-full"><button class="w-full text-left hover:opacity-90 transition-opacity cursor-pointer" aria-label="View all participants"><!></button></section> <!>`, 1);
var root = $.from_html(`<div class="flex flex-col items-center space-y-4 w-full max-w-[520px] mx-auto"><!> <!></div>`);

export default function Split($$anchor, $$props) {
	$.push($$props, true);

	// Split Dashboard - Participant-Centric View
	// Story FNS-002-2: Main Dashboard with Participant Cards
	// Extract splitId from route params
	const splitId = $.derived(() => route.params.splitId || '');

	// State
	let split = $.state(null);

	let splitDisplayName = $.state($.proxy(getCachedSplitName(route.params.splitId || '')));
	let isLoading = $.state(true);
	let showLoading = $.state(false);
	let loadingTimer = null;
	let error = $.state(null);

	// Expense summary stats
	const expenseCount = $.derived(() => $.get(split)?.expenses.length ?? 0);

	const expenseTotal = $.derived(() => $.get(split)?.expenses.reduce((sum, e) => sum + e.amount, 0) ?? 0);

	// Settlement status
	const isSettled = $.derived(() => $.get(split)?.settlement != null);

	// Participant count (used for settlement card visibility)
	const participantCount = $.derived(() => $.get(split)?.participants.length ?? 0);

	// Fetch split data when splitId changes
	$.user_effect(() => {
		if ($.get(splitId)) {
			loadSplit($.get(splitId));
		}
	});

	async function loadSplit(id) {
		$.set(isLoading, true);
		$.set(showLoading, false);
		$.set(error, null);

		if (loadingTimer) clearTimeout(loadingTimer);

		loadingTimer = setTimeout(
			() => {
				$.set(showLoading, true);
			},
			500
		);

		try {
			$.set(split, await getSplit(id), true);
			$.set(splitDisplayName, $.get(split).name, true);
			setCachedSplitName(id, $.get(split).name);
			saveLastSplit({ id: $.get(split).id, name: $.get(split).name });
		} catch(err) {
			const apiError = err;

			if (apiError.status === 404 || apiError.status === 400) {
				addToast({ type: 'info', message: 'Split not found — create a new one.' });
				navigate('/');
			} else {
				$.set(error, apiError.detail || 'Failed to load split. Please try again.', true);
			}
		} finally {
			$.set(isLoading, false);
			$.set(showLoading, false);

			if (loadingTimer) {
				clearTimeout(loadingTimer);
				loadingTimer = null;
			}
		}
	}

	function formatCurrency(amount) {
		return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR' }).format(amount);
	}

	var div = root();
	var node = $.child(div);

	{
		var consequent = ($$anchor) => {
			SplitPageHeader($$anchor, {
				get splitName() {
					return $.get(splitDisplayName);
				},

				get splitId() {
					return $.get(splitId);
				}
			});
		};

		$.if(node, ($$render) => {
			if ($.get(splitId)) $$render(consequent);
		});
	}

	var node_1 = $.sibling(node, 2);

	{
		var consequent_1 = ($$anchor) => {
			var div_1 = root_2();

			$.append($$anchor, div_1);
		};

		var alternate_1 = ($$anchor) => {
			var fragment_1 = $.comment();
			var node_2 = $.first_child(fragment_1);

			{
				var consequent_2 = ($$anchor) => {
					var fragment_2 = $.comment();
					var node_3 = $.first_child(fragment_2);

					$.component(node_3, () => Card.Root, ($$anchor, Card_Root) => {
						Card_Root($$anchor, {
							class: 'w-full',

							children: ($$anchor, $$slotProps) => {
								var fragment_3 = root_5();
								var node_4 = $.first_child(fragment_3);

								$.component(node_4, () => Card.Header, ($$anchor, Card_Header) => {
									Card_Header($$anchor, {
										children: ($$anchor, $$slotProps) => {
											var fragment_4 = $.comment();
											var node_5 = $.first_child(fragment_4);

											$.component(node_5, () => Card.Title, ($$anchor, Card_Title) => {
												Card_Title($$anchor, {
													class: 'text-center text-destructive',

													children: ($$anchor, $$slotProps) => {
														$.next();

														var text = $.text('Error loading split');

														$.append($$anchor, text);
													},

													$$slots: { default: true }
												});
											});

											$.append($$anchor, fragment_4);
										},

										$$slots: { default: true }
									});
								});

								var node_6 = $.sibling(node_4, 2);

								$.component(node_6, () => Card.Content, ($$anchor, Card_Content) => {
									Card_Content($$anchor, {
										class: 'text-center space-y-4',

										children: ($$anchor, $$slotProps) => {
											var fragment_5 = root_8();
											var p = $.first_child(fragment_5);
											var text_1 = $.child(p, true);

											$.reset(p);

											var node_7 = $.sibling(p, 2);

											Button(node_7, {
												onclick: () => loadSplit($.get(splitId)),
												class: 'min-h-[44px]',

												children: ($$anchor, $$slotProps) => {
													$.next();

													var text_2 = $.text('Retry');

													$.append($$anchor, text_2);
												},

												$$slots: { default: true }
											});

											$.template_effect(() => $.set_text(text_1, $.get(error)));
											$.append($$anchor, fragment_5);
										},

										$$slots: { default: true }
									});
								});

								$.append($$anchor, fragment_3);
							},

							$$slots: { default: true }
						});
					});

					$.append($$anchor, fragment_2);
				};

				var alternate = ($$anchor) => {
					var fragment_6 = $.comment();
					var node_8 = $.first_child(fragment_6);

					{
						var consequent_4 = ($$anchor) => {
							var fragment_7 = root_11();
							var section = $.first_child(fragment_7);
							var button = $.child(section);

							button.__click = () => navigate(`/splits/${$.get(splitId)}/expenses`);

							var node_9 = $.child(button);

							$.component(node_9, () => Card.Root, ($$anchor, Card_Root_1) => {
								Card_Root_1($$anchor, {
									class: 'border-teal-200 bg-teal-50/50 hover:bg-teal-50 transition-colors cursor-pointer',

									children: ($$anchor, $$slotProps) => {
										var fragment_8 = $.comment();
										var node_10 = $.first_child(fragment_8);

										$.component(node_10, () => Card.Content, ($$anchor, Card_Content_1) => {
											Card_Content_1($$anchor, {
												class: 'py-4',

												children: ($$anchor, $$slotProps) => {
													var div_2 = root_13();
													var div_3 = $.child(div_2);
													var p_1 = $.sibling($.child(div_3), 2);
													var text_3 = $.child(p_1);

													$.reset(p_1);
													$.reset(div_3);

													var div_4 = $.sibling(div_3, 2);
													var p_2 = $.sibling($.child(div_4), 2);
													var text_4 = $.child(p_2, true);

													$.reset(p_2);
													$.reset(div_4);
													$.reset(div_2);

													$.template_effect(
														($0) => {
															$.set_text(text_3, `${$.get(expenseCount) ?? ''} ${$.get(expenseCount) === 1 ? 'expense' : 'expenses'}`);
															$.set_text(text_4, $0);
														},
														[() => formatCurrency($.get(expenseTotal))]
													);

													$.append($$anchor, div_2);
												},

												$$slots: { default: true }
											});
										});

										$.append($$anchor, fragment_8);
									},

									$$slots: { default: true }
								});
							});

							$.reset(button);
							$.reset(section);

							var section_1 = $.sibling(section, 2);
							var button_1 = $.child(section_1);

							button_1.__click = () => navigate(`/splits/${$.get(splitId)}/participants`);

							var node_11 = $.child(button_1);

							ParticipantSummaryCard(node_11, {
								get participants() {
									return $.get(split).participants;
								}
							});

							$.reset(button_1);
							$.reset(section_1);

							var node_12 = $.sibling(section_1, 2);

							{
								var consequent_3 = ($$anchor) => {
									var section_2 = root_14();
									var button_2 = $.child(section_2);

									button_2.__click = () => {
										navigate(`/splits/${$.get(splitId)}/settlement`);
									};

									var node_13 = $.child(button_2);

									$.component(node_13, () => Card.Root, ($$anchor, Card_Root_2) => {
										Card_Root_2($$anchor, {
											class: 'border-teal-200 bg-teal-50/50 hover:bg-teal-50 transition-colors cursor-pointer',

											children: ($$anchor, $$slotProps) => {
												var fragment_9 = $.comment();
												var node_14 = $.first_child(fragment_9);

												$.component(node_14, () => Card.Content, ($$anchor, Card_Content_2) => {
													Card_Content_2($$anchor, {
														class: 'py-4',

														children: ($$anchor, $$slotProps) => {
															var div_5 = root_16();
															var div_6 = $.child(div_5);
															var p_3 = $.sibling($.child(div_6), 2);
															var text_5 = $.child(p_3, true);

															$.reset(p_3);
															$.reset(div_6);
															$.next(2);
															$.reset(div_5);
															$.template_effect(() => $.set_text(text_5, $.get(isSettled) ? 'Settled' : 'Solve'));
															$.append($$anchor, div_5);
														},

														$$slots: { default: true }
													});
												});

												$.append($$anchor, fragment_9);
											},

											$$slots: { default: true }
										});
									});

									$.reset(button_2);
									$.reset(section_2);
									$.append($$anchor, section_2);
								};

								$.if(node_12, ($$render) => {
									if ($.get(expenseCount) > 0 && $.get(participantCount) > 0) $$render(consequent_3);
								});
							}

							$.append($$anchor, fragment_7);
						};

						$.if(
							node_8,
							($$render) => {
								if (!$.get(isLoading) && $.get(split)) $$render(consequent_4);
							},
							true
						);
					}

					$.append($$anchor, fragment_6);
				};

				$.if(
					node_2,
					($$render) => {
						if ($.get(error)) $$render(consequent_2); else $$render(alternate, false);
					},
					true
				);
			}

			$.append($$anchor, fragment_1);
		};

		$.if(node_1, ($$render) => {
			if ($.get(showLoading)) $$render(consequent_1); else $$render(alternate_1, false);
		});
	}

	$.reset(div);
	$.append($$anchor, div);
	$.pop();
}

$.delegate(['click']);
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJuYW1lcyI6W10sInNvdXJjZXMiOlsiU3BsaXQuc3ZlbHRlIl0sInNvdXJjZXNDb250ZW50IjpbIjxzY3JpcHQgbGFuZz1cInRzXCI+XG4gIC8vIFNwbGl0IERhc2hib2FyZCAtIFBhcnRpY2lwYW50LUNlbnRyaWMgVmlld1xuICAvLyBTdG9yeSBGTlMtMDAyLTI6IE1haW4gRGFzaGJvYXJkIHdpdGggUGFydGljaXBhbnQgQ2FyZHNcblxuICBpbXBvcnQgeyBnZXRTcGxpdCwgdHlwZSBTcGxpdCB9IGZyb20gJyRsaWIvYXBpL3NwbGl0cyc7XG4gIGltcG9ydCB0eXBlIHsgQXBpRXJyb3IgfSBmcm9tICckbGliL2FwaS9jbGllbnQnO1xuICBpbXBvcnQgeyBCdXR0b24gfSBmcm9tICckbGliL2NvbXBvbmVudHMvdWkvYnV0dG9uJztcbiAgaW1wb3J0ICogYXMgQ2FyZCBmcm9tICckbGliL2NvbXBvbmVudHMvdWkvY2FyZCc7XG4gIGltcG9ydCB7IGFkZFRvYXN0IH0gZnJvbSAnJGxpYi9zdG9yZXMvdG9hc3RTdG9yZS5zdmVsdGUnO1xuICBpbXBvcnQgeyByb3V0ZSwgbmF2aWdhdGUgfSBmcm9tICckbGliL3JvdXRlcic7XG4gIGltcG9ydCB7IHNhdmVMYXN0U3BsaXQgfSBmcm9tICckbGliL3N0b3Jlcy9sYXN0U3BsaXRTdG9yZSc7XG4gIGltcG9ydCB7IGdldENhY2hlZFNwbGl0TmFtZSwgc2V0Q2FjaGVkU3BsaXROYW1lIH0gZnJvbSAnJGxpYi9zdG9yZXMvc3BsaXRUaXRsZUNhY2hlJztcbiAgaW1wb3J0IFBhcnRpY2lwYW50U3VtbWFyeUNhcmQgZnJvbSAnJGxpYi9jb21wb25lbnRzL3BhcnRpY2lwYW50L1BhcnRpY2lwYW50U3VtbWFyeUNhcmQuc3ZlbHRlJztcbiAgaW1wb3J0IFNwbGl0UGFnZUhlYWRlciBmcm9tICckbGliL2NvbXBvbmVudHMvdWkvc3BsaXQtcGFnZS1oZWFkZXIvU3BsaXRQYWdlSGVhZGVyLnN2ZWx0ZSc7XG5cbiAgLy8gRXh0cmFjdCBzcGxpdElkIGZyb20gcm91dGUgcGFyYW1zXG4gIGNvbnN0IHNwbGl0SWQgPSAkZGVyaXZlZChyb3V0ZS5wYXJhbXMuc3BsaXRJZCB8fCAnJyk7XG5cbiAgLy8gU3RhdGVcbiAgbGV0IHNwbGl0ID0gJHN0YXRlPFNwbGl0IHwgbnVsbD4obnVsbCk7XG4gIGxldCBzcGxpdERpc3BsYXlOYW1lID0gJHN0YXRlKGdldENhY2hlZFNwbGl0TmFtZShyb3V0ZS5wYXJhbXMuc3BsaXRJZCB8fCAnJykpO1xuICBsZXQgaXNMb2FkaW5nID0gJHN0YXRlKHRydWUpO1xuICBsZXQgc2hvd0xvYWRpbmcgPSAkc3RhdGUoZmFsc2UpO1xuICBsZXQgbG9hZGluZ1RpbWVyOiBSZXR1cm5UeXBlPHR5cGVvZiBzZXRUaW1lb3V0PiB8IG51bGwgPSBudWxsO1xuICBsZXQgZXJyb3IgPSAkc3RhdGU8c3RyaW5nIHwgbnVsbD4obnVsbCk7XG5cbiAgLy8gRXhwZW5zZSBzdW1tYXJ5IHN0YXRzXG4gIGNvbnN0IGV4cGVuc2VDb3VudCA9ICRkZXJpdmVkKHNwbGl0Py5leHBlbnNlcy5sZW5ndGggPz8gMCk7XG4gIGNvbnN0IGV4cGVuc2VUb3RhbCA9ICRkZXJpdmVkKFxuICAgIHNwbGl0Py5leHBlbnNlcy5yZWR1Y2UoKHN1bSwgZSkgPT4gc3VtICsgZS5hbW91bnQsIDApID8/IDBcbiAgKTtcblxuICAvLyBTZXR0bGVtZW50IHN0YXR1c1xuICBjb25zdCBpc1NldHRsZWQgPSAkZGVyaXZlZChzcGxpdD8uc2V0dGxlbWVudCAhPSBudWxsKTtcblxuICAvLyBQYXJ0aWNpcGFudCBjb3VudCAodXNlZCBmb3Igc2V0dGxlbWVudCBjYXJkIHZpc2liaWxpdHkpXG4gIGNvbnN0IHBhcnRpY2lwYW50Q291bnQgPSAkZGVyaXZlZChzcGxpdD8ucGFydGljaXBhbnRzLmxlbmd0aCA/PyAwKTtcblxuICAvLyBGZXRjaCBzcGxpdCBkYXRhIHdoZW4gc3BsaXRJZCBjaGFuZ2VzXG4gICRlZmZlY3QoKCkgPT4ge1xuICAgIGlmIChzcGxpdElkKSB7XG4gICAgICBsb2FkU3BsaXQoc3BsaXRJZCk7XG4gICAgfVxuICB9KTtcblxuICBhc3luYyBmdW5jdGlvbiBsb2FkU3BsaXQoaWQ6IHN0cmluZykge1xuICAgIGlzTG9hZGluZyA9IHRydWU7XG4gICAgc2hvd0xvYWRpbmcgPSBmYWxzZTtcbiAgICBlcnJvciA9IG51bGw7XG5cbiAgICBpZiAobG9hZGluZ1RpbWVyKSBjbGVhclRpbWVvdXQobG9hZGluZ1RpbWVyKTtcbiAgICBsb2FkaW5nVGltZXIgPSBzZXRUaW1lb3V0KCgpID0+IHsgc2hvd0xvYWRpbmcgPSB0cnVlOyB9LCA1MDApO1xuXG4gICAgdHJ5IHtcbiAgICAgIHNwbGl0ID0gYXdhaXQgZ2V0U3BsaXQoaWQpO1xuICAgICAgc3BsaXREaXNwbGF5TmFtZSA9IHNwbGl0Lm5hbWU7XG4gICAgICBzZXRDYWNoZWRTcGxpdE5hbWUoaWQsIHNwbGl0Lm5hbWUpO1xuICAgICAgc2F2ZUxhc3RTcGxpdCh7IGlkOiBzcGxpdC5pZCwgbmFtZTogc3BsaXQubmFtZSB9KTtcbiAgICB9IGNhdGNoIChlcnIpIHtcbiAgICAgIGNvbnN0IGFwaUVycm9yID0gZXJyIGFzIEFwaUVycm9yO1xuICAgICAgaWYgKGFwaUVycm9yLnN0YXR1cyA9PT0gNDA0IHx8IGFwaUVycm9yLnN0YXR1cyA9PT0gNDAwKSB7XG4gICAgICAgIGFkZFRvYXN0KHsgdHlwZTogJ2luZm8nLCBtZXNzYWdlOiAnU3BsaXQgbm90IGZvdW5kIOKAlCBjcmVhdGUgYSBuZXcgb25lLicgfSk7XG4gICAgICAgIG5hdmlnYXRlKCcvJyk7XG4gICAgICB9IGVsc2Uge1xuICAgICAgICBlcnJvciA9IGFwaUVycm9yLmRldGFpbCB8fCAnRmFpbGVkIHRvIGxvYWQgc3BsaXQuIFBsZWFzZSB0cnkgYWdhaW4uJztcbiAgICAgIH1cbiAgICB9IGZpbmFsbHkge1xuICAgICAgaXNMb2FkaW5nID0gZmFsc2U7XG4gICAgICBzaG93TG9hZGluZyA9IGZhbHNlO1xuICAgICAgaWYgKGxvYWRpbmdUaW1lcikgeyBjbGVhclRpbWVvdXQobG9hZGluZ1RpbWVyKTsgbG9hZGluZ1RpbWVyID0gbnVsbDsgfVxuICAgIH1cbiAgfVxuXG4gIGZ1bmN0aW9uIGZvcm1hdEN1cnJlbmN5KGFtb3VudDogbnVtYmVyKTogc3RyaW5nIHtcbiAgICByZXR1cm4gbmV3IEludGwuTnVtYmVyRm9ybWF0KCdlbi1JRScsIHtcbiAgICAgIHN0eWxlOiAnY3VycmVuY3knLFxuICAgICAgY3VycmVuY3k6ICdFVVInLFxuICAgIH0pLmZvcm1hdChhbW91bnQpO1xuICB9XG5cblxuPC9zY3JpcHQ+XG5cbjxkaXYgY2xhc3M9XCJmbGV4IGZsZXgtY29sIGl0ZW1zLWNlbnRlciBzcGFjZS15LTQgdy1mdWxsIG1heC13LVs1MjBweF0gbXgtYXV0b1wiPlxuICA8IS0tIEhlYWRlciBhbHdheXMgdmlzaWJsZSBvbmNlIHNwbGl0SWQgaXMga25vd24gLS0+XG4gIHsjaWYgc3BsaXRJZH1cbiAgICA8U3BsaXRQYWdlSGVhZGVyIHNwbGl0TmFtZT17c3BsaXREaXNwbGF5TmFtZX0ge3NwbGl0SWR9IC8+XG4gIHsvaWZ9XG5cbiAgeyNpZiBzaG93TG9hZGluZ31cbiAgICA8IS0tIExvYWRpbmcgU3RhdGUgKGRlZmVycmVkIDUwMG1zIHRvIGF2b2lkIGZsYXNoKSAtLT5cbiAgICA8ZGl2IGNsYXNzPVwiZmxleCBmbGV4LWNvbCBpdGVtcy1jZW50ZXIganVzdGlmeS1jZW50ZXIgcHktMTIgc3BhY2UteS00XCI+XG4gICAgICA8c3ZnIGNsYXNzPVwiYW5pbWF0ZS1zcGluIGgtOCB3LTggdGV4dC1wcmltYXJ5XCIgeG1sbnM9XCJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Z1wiIGZpbGw9XCJub25lXCIgdmlld0JveD1cIjAgMCAyNCAyNFwiPlxuICAgICAgICA8Y2lyY2xlIGNsYXNzPVwib3BhY2l0eS0yNVwiIGN4PVwiMTJcIiBjeT1cIjEyXCIgcj1cIjEwXCIgc3Ryb2tlPVwiY3VycmVudENvbG9yXCIgc3Ryb2tlLXdpZHRoPVwiNFwiPjwvY2lyY2xlPlxuICAgICAgICA8cGF0aCBjbGFzcz1cIm9wYWNpdHktNzVcIiBmaWxsPVwiY3VycmVudENvbG9yXCIgZD1cIk00IDEyYTggOCAwIDAxOC04VjBDNS4zNzMgMCAwIDUuMzczIDAgMTJoNHptMiA1LjI5MUE3Ljk2MiA3Ljk2MiAwIDAxNCAxMkgwYzAgMy4wNDIgMS4xMzUgNS44MjQgMyA3LjkzOGwzLTIuNjQ3elwiPjwvcGF0aD5cbiAgICAgIDwvc3ZnPlxuICAgICAgPHAgY2xhc3M9XCJ0ZXh0LW11dGVkLWZvcmVncm91bmRcIj5Mb2FkaW5nIHNwbGl0Li4uPC9wPlxuICAgIDwvZGl2PlxuXG4gIHs6ZWxzZSBpZiBlcnJvcn1cbiAgICA8IS0tIEVycm9yIFN0YXRlIC0tPlxuICAgIDxDYXJkLlJvb3QgY2xhc3M9XCJ3LWZ1bGxcIj5cbiAgICAgIDxDYXJkLkhlYWRlcj5cbiAgICAgICAgPENhcmQuVGl0bGUgY2xhc3M9XCJ0ZXh0LWNlbnRlciB0ZXh0LWRlc3RydWN0aXZlXCI+RXJyb3IgbG9hZGluZyBzcGxpdDwvQ2FyZC5UaXRsZT5cbiAgICAgIDwvQ2FyZC5IZWFkZXI+XG4gICAgICA8Q2FyZC5Db250ZW50IGNsYXNzPVwidGV4dC1jZW50ZXIgc3BhY2UteS00XCI+XG4gICAgICAgIDxwIGNsYXNzPVwidGV4dC1tdXRlZC1mb3JlZ3JvdW5kXCI+e2Vycm9yfTwvcD5cbiAgICAgICAgPEJ1dHRvbiBvbmNsaWNrPXsoKSA9PiBsb2FkU3BsaXQoc3BsaXRJZCl9IGNsYXNzPVwibWluLWgtWzQ0cHhdXCI+XG4gICAgICAgICAgUmV0cnlcbiAgICAgICAgPC9CdXR0b24+XG4gICAgICA8L0NhcmQuQ29udGVudD5cbiAgICA8L0NhcmQuUm9vdD5cblxuICB7OmVsc2UgaWYgIWlzTG9hZGluZyAmJiBzcGxpdH1cbiAgICA8IS0tIEV4cGVuc2UgU3VtbWFyeSBDYXJkIChjbGlja2FibGUg4oaSIG5hdmlnYXRlcyB0byBleHBlbnNlIGxpc3QpIC0tPlxuICAgIDxzZWN0aW9uIGNsYXNzPVwidy1mdWxsXCI+XG4gICAgICA8YnV0dG9uXG4gICAgICAgIGNsYXNzPVwidy1mdWxsIHRleHQtbGVmdFwiXG4gICAgICAgIG9uY2xpY2s9eygpID0+IG5hdmlnYXRlKGAvc3BsaXRzLyR7c3BsaXRJZH0vZXhwZW5zZXNgKX1cbiAgICAgICAgYXJpYS1sYWJlbD1cIlZpZXcgYWxsIGV4cGVuc2VzXCJcbiAgICAgID5cbiAgICAgICAgPENhcmQuUm9vdCBjbGFzcz1cImJvcmRlci10ZWFsLTIwMCBiZy10ZWFsLTUwLzUwIGhvdmVyOmJnLXRlYWwtNTAgdHJhbnNpdGlvbi1jb2xvcnMgY3Vyc29yLXBvaW50ZXJcIj5cbiAgICAgICAgICA8Q2FyZC5Db250ZW50IGNsYXNzPVwicHktNFwiPlxuICAgICAgICAgICAgPGRpdiBjbGFzcz1cImZsZXggaXRlbXMtY2VudGVyIGp1c3RpZnktYmV0d2VlblwiPlxuICAgICAgICAgICAgICA8ZGl2PlxuICAgICAgICAgICAgICAgIDxwIGNsYXNzPVwidGV4dC1zbSB0ZXh0LW11dGVkLWZvcmVncm91bmRcIj5FeHBlbnNlczwvcD5cbiAgICAgICAgICAgICAgICA8cCBjbGFzcz1cInRleHQtbGcgZm9udC1zZW1pYm9sZFwiPntleHBlbnNlQ291bnR9IHtleHBlbnNlQ291bnQgPT09IDEgPyAnZXhwZW5zZScgOiAnZXhwZW5zZXMnfTwvcD5cbiAgICAgICAgICAgICAgPC9kaXY+XG4gICAgICAgICAgICAgIDxkaXYgY2xhc3M9XCJ0ZXh0LXJpZ2h0XCI+XG4gICAgICAgICAgICAgICAgPHAgY2xhc3M9XCJ0ZXh0LXNtIHRleHQtbXV0ZWQtZm9yZWdyb3VuZFwiPlRvdGFsPC9wPlxuICAgICAgICAgICAgICAgIDxwIGNsYXNzPVwidGV4dC1sZyBmb250LXNlbWlib2xkIHRleHQtcHJpbWFyeVwiPntmb3JtYXRDdXJyZW5jeShleHBlbnNlVG90YWwpfTwvcD5cbiAgICAgICAgICAgICAgPC9kaXY+XG4gICAgICAgICAgICA8L2Rpdj5cbiAgICAgICAgICA8L0NhcmQuQ29udGVudD5cbiAgICAgICAgPC9DYXJkLlJvb3Q+XG4gICAgICA8L2J1dHRvbj5cbiAgICA8L3NlY3Rpb24+XG5cbiAgICA8IS0tIFBhcnRpY2lwYW50cyBTdW1tYXJ5IENhcmQgKGNsaWNrYWJsZSDihpIgbmF2aWdhdGVzIHRvIHBhcnRpY2lwYW50cyBwYWdlKSAtLT5cbiAgICA8c2VjdGlvbiBjbGFzcz1cInctZnVsbFwiPlxuICAgICAgPGJ1dHRvblxuICAgICAgICBjbGFzcz1cInctZnVsbCB0ZXh0LWxlZnQgaG92ZXI6b3BhY2l0eS05MCB0cmFuc2l0aW9uLW9wYWNpdHkgY3Vyc29yLXBvaW50ZXJcIlxuICAgICAgICBvbmNsaWNrPXsoKSA9PiBuYXZpZ2F0ZShgL3NwbGl0cy8ke3NwbGl0SWR9L3BhcnRpY2lwYW50c2ApfVxuICAgICAgICBhcmlhLWxhYmVsPVwiVmlldyBhbGwgcGFydGljaXBhbnRzXCJcbiAgICAgID5cbiAgICAgICAgPFBhcnRpY2lwYW50U3VtbWFyeUNhcmQgcGFydGljaXBhbnRzPXtzcGxpdC5wYXJ0aWNpcGFudHN9IC8+XG4gICAgICA8L2J1dHRvbj5cbiAgICA8L3NlY3Rpb24+XG5cbiAgICA8IS0tIFNvbHZlIENhcmQgKGNsaWNrYWJsZSDihpIgbmF2aWdhdGVzIHRvIHNldHRsZW1lbnQgcGFnZSkgLS0+XG4gICAgeyNpZiBleHBlbnNlQ291bnQgPiAwICYmIHBhcnRpY2lwYW50Q291bnQgPiAwfVxuICAgICAgPHNlY3Rpb24gY2xhc3M9XCJ3LWZ1bGxcIj5cbiAgICAgICAgPGJ1dHRvblxuICAgICAgICAgIGNsYXNzPVwidy1mdWxsIHRleHQtbGVmdFwiXG4gICAgICAgICAgb25jbGljaz17KCkgPT4ge1xuICAgICAgICAgICAgbmF2aWdhdGUoYC9zcGxpdHMvJHtzcGxpdElkfS9zZXR0bGVtZW50YCk7XG4gICAgICAgICAgfX1cbiAgICAgICAgICBhcmlhLWxhYmVsPVwiVmlldyBzZXR0bGVtZW50XCJcbiAgICAgICAgPlxuICAgICAgICAgIDxDYXJkLlJvb3QgY2xhc3M9XCJib3JkZXItdGVhbC0yMDAgYmctdGVhbC01MC81MCBob3ZlcjpiZy10ZWFsLTUwIHRyYW5zaXRpb24tY29sb3JzIGN1cnNvci1wb2ludGVyXCI+XG4gICAgICAgICAgICA8Q2FyZC5Db250ZW50IGNsYXNzPVwicHktNFwiPlxuICAgICAgICAgICAgICA8ZGl2IGNsYXNzPVwiZmxleCBpdGVtcy1jZW50ZXIganVzdGlmeS1iZXR3ZWVuXCI+XG4gICAgICAgICAgICAgICAgPGRpdj5cbiAgICAgICAgICAgICAgICAgIDxwIGNsYXNzPVwidGV4dC1zbSB0ZXh0LW11dGVkLWZvcmVncm91bmRcIj5TZXR0bGVtZW50PC9wPlxuICAgICAgICAgICAgICAgICAgPHAgY2xhc3M9XCJ0ZXh0LWxnIGZvbnQtc2VtaWJvbGRcIj57aXNTZXR0bGVkID8gJ1NldHRsZWQnIDogJ1NvbHZlJ308L3A+XG4gICAgICAgICAgICAgICAgPC9kaXY+XG4gICAgICAgICAgICAgICAgPHN2ZyB4bWxucz1cImh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnXCIgY2xhc3M9XCJoLTYgdy02IHRleHQtcHJpbWFyeVwiIGZpbGw9XCJub25lXCIgdmlld0JveD1cIjAgMCAyNCAyNFwiIHN0cm9rZT1cImN1cnJlbnRDb2xvclwiIHN0cm9rZS13aWR0aD1cIjJcIj5cbiAgICAgICAgICAgICAgICAgIDxwYXRoIHN0cm9rZS1saW5lY2FwPVwicm91bmRcIiBzdHJva2UtbGluZWpvaW49XCJyb3VuZFwiIGQ9XCJNOSA1bDcgNy03IDdcIiAvPlxuICAgICAgICAgICAgICAgIDwvc3ZnPlxuICAgICAgICAgICAgICA8L2Rpdj5cbiAgICAgICAgICAgIDwvQ2FyZC5Db250ZW50PlxuICAgICAgICAgIDwvQ2FyZC5Sb290PlxuICAgICAgICA8L2J1dHRvbj5cbiAgICAgIDwvc2VjdGlvbj5cbiAgICB7L2lmfVxuXG4gIHsvaWZ9XG48L2Rpdj5cbiJdLCJtYXBwaW5ncyI6Ijs7U0FJVyxRQUFRLFFBQW9CLGlCQUFpQjtTQUU3QyxNQUFNLFFBQVEsMkJBQTJCO09BQzNDLFNBQVMsTUFBTSx5QkFBeUI7U0FDdEMsUUFBUSxRQUFRLCtCQUErQjtTQUMvQyxLQUFLLEVBQUUsUUFBUSxRQUFRLGFBQWE7U0FDcEMsYUFBYSxRQUFRLDRCQUE0QjtTQUNqRCxrQkFBa0IsRUFBRSxrQkFBa0IsUUFBUSw2QkFBNkI7T0FDN0Usc0JBQXNCLE1BQU0sMkRBQTJEO09BQ3ZGLGVBQWUsTUFBTSw2REFBNkQ7Ozs7Ozs7Ozs7O2lEQWIzRixDQUFDOzs7Ozs7T0FnQk8sT0FBTyxtQkFBWSxLQUFLLENBQUMsTUFBTSxDQUFDLE9BQU8sSUFBSSxFQUFFOzs7S0FHL0MsS0FBSyxXQUF3QixJQUFJOztLQUNqQyxnQkFBZ0IsbUJBQVUsa0JBQWtCLENBQUMsS0FBSyxDQUFDLE1BQU0sQ0FBQyxPQUFPLElBQUksRUFBRTtLQUN2RSxTQUFTLFdBQVUsSUFBSTtLQUN2QixXQUFXLFdBQVUsS0FBSztLQUMxQixZQUFrRCxHQUFHLElBQUk7S0FDekQsS0FBSyxXQUF5QixJQUFJOzs7T0FHaEMsWUFBWSx5QkFBWSxLQUFLLEdBQUUsUUFBUSxDQUFDLE1BQU0sSUFBSSxDQUFDOztPQUNuRCxZQUFZLHlCQUNoQixLQUFLLEdBQUUsUUFBUSxDQUFDLE1BQU0sRUFBRSxHQUFHLEVBQUUsQ0FBQyxLQUFLLEdBQUcsR0FBRyxDQUFDLENBQUMsTUFBTSxFQUFFLENBQUMsS0FBSyxDQUFDOzs7T0FJdEQsU0FBUyx5QkFBWSxLQUFLLEdBQUUsVUFBVSxJQUFJLElBQUk7OztPQUc5QyxnQkFBZ0IseUJBQVksS0FBSyxHQUFFLFlBQVksQ0FBQyxNQUFNLElBQUksQ0FBQzs7O0NBR2pFLGFBQU8sT0FBTyxDQUFDO1lBQ1QsT0FBTyxHQUFFLENBQUM7R0FDWixTQUFTLE9BQUMsT0FBTztFQUNuQixDQUFDO0NBQ0gsQ0FBQzs7Z0JBRWMsU0FBUyxDQUFDLEVBQVUsRUFBRSxDQUFDO1FBQ3BDLFNBQVMsRUFBRyxJQUFJO1FBQ2hCLFdBQVcsRUFBRyxLQUFLO1FBQ25CLEtBQUssRUFBRyxJQUFJOztNQUVSLFlBQVksRUFBRSxZQUFZLENBQUMsWUFBWTs7RUFDM0MsWUFBWSxHQUFHLFVBQVU7U0FBTyxDQUFDO1VBQUMsV0FBVyxFQUFHLElBQUk7R0FBRSxDQUFDO0dBQUUsR0FBRzs7O01BRXhELENBQUM7U0FDSCxLQUFLLFFBQVMsUUFBUSxDQUFDLEVBQUU7U0FDekIsZ0JBQWdCLFFBQUcsS0FBSyxFQUFDLElBQUk7R0FDN0Isa0JBQWtCLENBQUMsRUFBRSxRQUFFLEtBQUssRUFBQyxJQUFJO0dBQ2pDLGFBQWEsR0FBRyxFQUFFLFFBQUUsS0FBSyxFQUFDLEVBQUUsRUFBRSxJQUFJLFFBQUUsS0FBSyxFQUFDLElBQUk7RUFDaEQsQ0FBQyxPQUFRLEdBQUcsRUFBRSxDQUFDO1NBQ1AsUUFBUSxHQUFHLEdBQUc7O09BQ2hCLFFBQVEsQ0FBQyxNQUFNLEtBQUssR0FBRyxJQUFJLFFBQVEsQ0FBQyxNQUFNLEtBQUssR0FBRyxFQUFFLENBQUM7SUFDdkQsUUFBUSxHQUFHLElBQUksRUFBRSxNQUFNLEVBQUUsT0FBTyxFQUFFLHFDQUFxQztJQUN2RSxRQUFRLENBQUMsR0FBRztHQUNkLENBQUMsTUFBTSxDQUFDO1VBQ04sS0FBSyxFQUFHLFFBQVEsQ0FBQyxNQUFNLElBQUkseUNBQXlDO0dBQ3RFLENBQUM7RUFDSCxDQUFDLFNBQVMsQ0FBQztTQUNULFNBQVMsRUFBRyxLQUFLO1NBQ2pCLFdBQVcsRUFBRyxLQUFLOztPQUNmLFlBQVksRUFBRSxDQUFDO0lBQUMsWUFBWSxDQUFDLFlBQVk7SUFBRyxZQUFZLEdBQUcsSUFBSTtHQUFFLENBQUM7RUFDeEUsQ0FBQztDQUNILENBQUM7O1VBRVEsY0FBYyxDQUFDLE1BQWMsRUFBVSxDQUFDO2FBQ3BDLElBQUksQ0FBQyxZQUFZLENBQUMsT0FBTyxJQUNsQyxLQUFLLEVBQUUsVUFBVSxFQUNqQixRQUFRLEVBQUUsS0FBSyxJQUNkLE1BQU0sQ0FBQyxNQUFNO0NBQ2xCLENBQUM7Ozs7Ozs7OztrQkFRNkIsZ0JBQWdCOzs7Ozs7Ozs7O2FBRHpDLE9BQU87Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OzsyQkFzQmlCLFNBQVMsT0FBQyxPQUFPOzs7Ozs7Ozs7Ozs7Ozs0REFETixLQUFLOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OzhCQVl4QixRQUFRLGtCQUFZLE9BQU87Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OzJDQVFBLFlBQVksaUJBQUcsWUFBWSxNQUFLLENBQUMsR0FBRyxTQUFTLEdBQUcsVUFBVTs7O3FCQUk3QyxjQUFjLE9BQUMsWUFBWTs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Z0NBWW5FLFFBQVEsa0JBQVksT0FBTzs7Ozs7O3NCQUdKLEtBQUssRUFBQyxZQUFZOzs7Ozs7Ozs7Ozs7OztrQ0FTdkMsQ0FBQztVQUNkLFFBQVEsa0JBQVksT0FBTztTQUM3QixDQUFDOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztnRUFReUMsU0FBUyxJQUFHLFNBQVMsR0FBRyxPQUFPOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7bUJBZDFFLFlBQVksSUFBRyxDQUFDLFVBQUksZ0JBQWdCLElBQUcsQ0FBQzs7Ozs7Ozs7OzttQkFyQ3BDLFNBQVMsV0FBSSxLQUFLOzs7Ozs7Ozs7Ozs7Z0JBZG5CLEtBQUs7Ozs7Ozs7Ozs7YUFWVixXQUFXOzs7Ozs7O0FBUm5CLENBQUM7OyJ9